import { PostgrestSingleResponse } from "@supabase/supabase-js";
import { supabase } from "../../providers/supabase/client";
import { Reservation, AerolineReservations, HotelReservations, ReservationRoom, ReservationTable } from "../../util/interfaces";

export const createReservation = async (reservation: Reservation) => {
  return await supabase.from("reservations").insert([reservation]).select(`
        *,
        clients(name),
        places(name,country)
    `);
};

export const createReservationCruise = async (cruiseData: any) => {
  return await supabase.from("reservations_cruises").insert([cruiseData]).select('*');
};

export const updateReservationCruise = async (id: number, cruiseData: any) => {
  return await supabase.from("reservations_cruises").update(cruiseData).eq("id", id).select('*');
};

export const createPassengers = async (passengers: any[]) => {
  return await supabase.from("passengers").insert(passengers).select('*');
};

export const createPaymentSchedules = async (paymentSchedules: any[]) => {
  return await supabase.from("payment_schedules").insert(paymentSchedules).select('*');
};

export const fetchPaymentSchedules = async (reservationId: string) => {
  return await supabase.from("payment_schedules").select('*').eq("reservation_id", reservationId).order("part_number", { ascending: true });
};

export const createReservationOptions = async (options: any[]) => {
  return await supabase.from("reservation_options").insert(options).select('*');
};

export const fetchReservationOptions = async (reservationId: string) => {
  return await supabase.from("reservation_options").select('*').eq("reservation_id", reservationId);
};

export const fetchPaymentsGeneric = async (reservationId: string) => {
  return await supabase.from("payments").select('*').eq("reservation_id", reservationId).order("paid_at", { ascending: false });
};

export const updateReservation = async (
  id: string,
  reservation: Reservation | { status: string }
) => {
  return await supabase.from("reservations").update(reservation).eq("id", id)
    .select(`
        *,
        clients(name),
        places(name,country)
    `);
};

export const deleteReservation = async (
  id: string
) => {
  return await supabase.from("reservations").delete().eq("id", id);
};

export const assignAerolineToReservation = async (
  aerolines: AerolineReservations[]
) => {
  return await supabase.from("reservations_aerolines").insert(aerolines)
    .select(`
        *,
        aerolines (id,name)
    `);
};


export const assignRoomsToReservation = async (
  rooms: ReservationRoom[]
) => {
  console.log({ rooms })
  return await supabase.from("reservation_rooms").insert(rooms)
    .select(`*`);
}

export const updateAerolineByReservation = async (
  id: number,
  aerolineReservation: AerolineReservations
) => {
  return await supabase
    .from("reservations_aerolines")
    .update(aerolineReservation)
    .eq("id", id).select(`
        *,
        aerolines (id,name)
    `);
};

export const deleteAerolineByReservation = async (id: number) => {
  return await supabase.from("reservations_aerolines").delete().eq("id", id);
};

export const assignHotelToReservation = async (hotels: HotelReservations[]) => {
  return await supabase.from("reservations_hotels").insert(hotels).select(`
        *,
        hotels(id,name),
        hotel_configurations(
            *,
            room_types(*)
        )
    `);
};

export const updateHotelByReservation = async (
  id: number,
  hotelReservation: HotelReservations
) => {
  return await supabase
    .from("reservations_hotels")
    .update(hotelReservation)
    .eq("id", id).select(`
          *,
          hotels (id,name)
      `);
};

export const deleteHotelByReservation = async (id: number) => {
  return await supabase.from("reservations_hotels").delete().eq("id", id);
};

export const assignToursToReservation = async (tours: any[]) => {
  return await supabase.from("reservations_tours").insert(tours).select(`*`);
};

export const updateToursByReservation = async (
  id: number,
  tourReservation: any
) => {
  return await supabase
    .from("reservations_tours")
    .update(tourReservation)
    .eq("id", id).select(`*`);
};

export const deleteTourByReservation = async (id: number) => {
  return await supabase.from("reservations_tours").delete().eq("id", id);
};

export const fetchReservations = async (userRole?: string): Promise<
  PostgrestSingleResponse<ReservationTable[]>
> => {
  let query = supabase.from("reservations").select(`
                *,
                clients(
                    name
                ),
                places (
                    id,
                    name,
                    country,
                    image_url,
                    cover_image_url,
                    slogan
                ),
                reservation_rooms (
                    id, reservations_id, room_number, adults, children, children_ages, infants, price
                ),
                reservations_aerolines (
                    id,
                    price,
                    adult_price,
                    kid_price,
                    infant_price,
                    airline_configuration_id,
                    aerolines (
                        id,
                        name
                    ),
                    airline_configurations (
                        id,
                        flight_types ( id, name )
                    )
                ),
                reservations_hotels (
                    id,
                    price,
                    adult_price,
                    kid_price,
                    toddler_price,
                    infant_price,
                    hotel_configuration_id,
                    hotels (
                        id,
                        name
                    ),
                    hotel_configurations (
                        id,
                        is_non_refundable,
                        meal_plan,
                        room_types ( id, name )
                    )
                ),
                reservations_tours (
                    id,
                    date,
                    adults,
                    children,
                    price,
                    adult_price,
                    kid_price,
                    infant_price,
                    tours (
                        id,
                        name,
                        adultPrice,
                        kidPrice,
                        min_age_kid,
                        max_age_kid
                    )
                ),
                payment_schedules (
                    id, reservation_id, part_number, amount, due_date, status
                ),
                promotions (
                    id
                )
             
        `);

  // All users see all reservations — advisor-level filtering
  // is handled client-side via the "Asesor" dropdown in the UI

  // Exclude cruise and flight reservations from the packages list
  query = query.or('type.is.null,and(type.neq.CRUISE,type.neq.FLIGHT,type.neq.HOTEL,type.neq.EXCURSION)');

  return await query.order('updated_at', { ascending: false });

};

// ─── Cruise Detail Services ──────────────────────────────────────────────────

export const fetchCruiseById = async (cruiseId: number) => {
  return await supabase
    .from("reservations_cruises")
    .select(`
      *,
      reservations (
        id, status, created_at, user_id, customer_id, total_price, start_date, end_date,
        clients ( id, name, first_name, last_name, email, phone, avatar_url, document_type, document_number, birth_date, document_issue_date, document_expiry_date, document_url )
      )
    `)
    .eq("id", cruiseId)
    .single();
};

export const fetchPackageById = async (reservationId: string) => {
  return await supabase
    .from("reservations")
    .select(`
      *,
      clients( id, name, first_name, last_name, email, phone, avatar_url, document_type, document_number, birth_date, document_issue_date, document_expiry_date, document_url ),
      places( id, name, country, cover_image_url, slogan ),
      reservation_rooms( * ),
      reservations_aerolines ( *, aerolines(*), airline_configurations(*, flight_types(*)) ),
      reservation_flight_services ( * ),
      reservations_hotels ( *, hotels(*), hotel_configurations(*, room_types(*)) ),
      reservations_tours ( *, tours(*) )
    `)
    .eq("id", reservationId)
    .single();
};

export const fetchPassengersByReservation = async (reservationId: string) => {
  return await supabase
    .from("passengers")
    .select("*")
    .eq("reservation_id", reservationId)
    .order("created_at", { ascending: true });
};

export const updatePassenger = async (id: string, data: any) => {
  return await supabase.from("passengers").update(data).eq("id", id).select("*");
};

export const deletePassenger = async (id: string) => {
  return await supabase.from("passengers").delete().eq("id", id);
};

// ─── Cruise Payments (UNIFIED — cruise now uses shared `payments` table) ─────
// fetchCruisePayments / createCruisePayment removed — use fetchPaymentsGeneric / reportPayment instead

// ─── Cruise Add-ons (Post-Services) ──────────────────────────────────────────

export const fetchCruiseAddons = async (reservationId: string) => {
  return await supabase
    .from("cruise_addons")
    .select("*")
    .eq("reservation_id", reservationId)
    .order("applied_at", { ascending: false });
};

export const createCruiseAddon = async (addon: any) => {
  return await supabase.from("cruise_addons").insert([addon]).select("*");
};

// ─── Audit Log (Reactivado con la tabla audit_logs global) ────

export const createAuditLog = async (entry: any) => {
  try {
    return await supabase.from("audit_logs").insert([{
      entity_type: "reservations",
      entity_id: entry.reservation_id ? String(entry.reservation_id) : null,
      reservation_id: entry.reservation_id || null,
      action: entry.action || "UPDATE",
      field_name: entry.field_name || null,
      old_value: entry.old_value || null,
      new_value: entry.new_value || null,
      changed_by: entry.changed_by || null,
      metadata: entry.metadata || {}
    }]).select("*");
  } catch (err) {
    console.warn("createAuditLog error:", err);
    return { data: null, error: err };
  }
};

export const fetchAuditLog = async (reservationId: string) => {
  try {
    return await supabase
      .from("audit_logs")
      .select("*")
      .or(`reservation_id.eq.${reservationId},and(entity_type.eq.reservations,entity_id.eq.${reservationId})`)
      .order("created_at", { ascending: false });
  } catch (err) {
    console.warn("fetchAuditLog error:", err);
    return { data: [], error: err };
  }
};

// ─── V4 Financial Recaudation Services ───────────────────────────────────────

export const reportPayment = async (paymentData: any) => {
  return await supabase.from("payments").insert([paymentData]).select("*");
};

export const verifyAndApprovePayment = async (paymentId: string, scheduleId: string, bankAccountId?: string, amount?: number, commercialExchangeRate?: number, paymentDate?: string) => {
  // Fetch existing payment to retrieve original paid_at date if no custom date is provided
  const { data: existingPayment } = await supabase
    .from("payments")
    .select("paid_at, created_at")
    .eq("id", paymentId)
    .maybeSingle();

  const originalPaidAt = existingPayment?.paid_at || existingPayment?.created_at;

  // 1. Mark schedule as PAGADO
  if (scheduleId) {
    const { error: sErr } = await supabase.from("payment_schedules").update({ status: 'PAGADO' }).eq('id', scheduleId);
    if (sErr) throw sErr;
  }
  
  // Normalize date-only strings (YYYY-MM-DD) to noon to prevent timezone day-shift
  const safePaymentDate = paymentDate && paymentDate.length === 10 ? `${paymentDate}T12:00:00` : paymentDate;
  
  // Fetch SUNAT exchange rate for the real payment date
  let sunatExchangeRate = null;
  try {
    const targetDateStr = paymentDate || (originalPaidAt ? new Date(originalPaidAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    const { data: exData } = await supabase
      .from("exchange_rates")
      .select("sell_rate")
      .eq("rate_date", targetDateStr)
      .maybeSingle();

    if (exData && exData.sell_rate) {
      sunatExchangeRate = parseFloat(exData.sell_rate);
    } else {
      const { data: latestEx } = await supabase
        .from("exchange_rates")
        .select("sell_rate")
        .order("rate_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (latestEx && latestEx.sell_rate) {
        sunatExchangeRate = parseFloat(latestEx.sell_rate);
      }
    }
  } catch (exErr) {
    console.warn("Failed to fetch SUNAT exchange rate:", exErr);
  }

  // 2. Mark payment receipt as PAGO_APROBADO and store exchange rates
  const resolvedPaidAt = safePaymentDate 
    ? new Date(safePaymentDate).toISOString() 
    : (originalPaidAt || new Date().toISOString());

  const updatePayload = {
    status: 'PAGO_APROBADO',
    verified_at: new Date().toISOString(),
    paid_at: resolvedPaidAt,
    commercial_exchange_rate: commercialExchangeRate || null,
    sunat_exchange_rate: sunatExchangeRate || null
  };

  const { data, error } = await supabase.from("payments").update(updatePayload).eq('id', paymentId).select('*');
  if (error) throw error;
  
  const payment = data?.[0];
  const paymentAmount = amount || Number(payment?.amount || 0);

  // 3. Register inbound treasury transaction if bankAccountId is provided
  if (bankAccountId && paymentAmount > 0) {
      const txDate = safePaymentDate || (originalPaidAt ? new Date(originalPaidAt).toISOString() : new Date().toISOString());
      
      // If client paid in PEN, use the actual soles amount; otherwise convert USD to PEN
      const isPenPayment = payment?.amount_pen && Number(payment.amount_pen) > 0;
      const txAmount = isPenPayment ? Number(payment.amount_pen) : paymentAmount;
      const txExchangeRate = isPenPayment 
          ? (Number(payment.payment_exchange_rate) || commercialExchangeRate || sunatExchangeRate || 1)
          : (commercialExchangeRate || sunatExchangeRate || 1);
      
      const { error: txErr } = await supabase.from("treasury_transactions").insert([{
          bank_account_id: bankAccountId,
          transaction_type: 'INBOUND',
          amount: txAmount,
          description: `Abono de Cliente (Voucher #${paymentId.substring(0, 6).toUpperCase()})${isPenPayment ? ` [PEN S/${Number(payment.amount_pen).toFixed(2)} @ TC ${txExchangeRate}]` : ''}`,
          reference_client_payment_id: paymentId,
          exchange_rate: txExchangeRate,
          transaction_date: txDate
      }]);
      if (txErr) console.error("Error creating treasury INBOUND tx:", txErr);
  }

  // 4. Auto-create VENTA invoice if one doesn't exist for this payment
  // This ensures the payment appears in the accounting module's "Ventas" section
  try {
    if (payment) {
      // Check if a VENTA invoice already exists for this payment (idempotency)
      const { data: existing } = await supabase
        .from("invoices")
        .select("id")
        .eq("payment_id", paymentId)
        .eq("flow_type", "VENTA")
        .maybeSingle();

      if (!existing) {
        // Get current user for RLS compliance
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        // Fetch reservation + client info for the invoice
        let clientName = '—';
        let clientId = null;
        let destination = '';
        let cuotaNumber = null;

        if (payment.reservation_id) {
          const { data: reservation } = await supabase
            .from("reservations")
            .select("id, clients(id, name, first_name, last_name), places(name)")
            .eq("id", payment.reservation_id)
            .maybeSingle();

          if (reservation?.clients) {
            const c = reservation.clients as any;
            clientName = (c.first_name || c.last_name)
              ? `${c.first_name || ''} ${c.last_name || ''}`.trim()
              : (c.name || '—');
            clientId = c.id;
          }
          destination = (reservation?.places as any)?.name || '';
        }

        if (payment.schedule_id) {
          const { data: schedule } = await supabase
            .from("payment_schedules")
            .select("part_number")
            .eq("id", payment.schedule_id)
            .maybeSingle();
          cuotaNumber = schedule?.part_number;
        }

        // Invoice currency matches the actual payment currency.
        // Per SUNAT: comprobantes can be issued in PEN or USD.
        // For IGV (PDT 621), USD invoices are converted to PEN using
        // SUNAT TC (sell rate) of the issue date at reporting time.
        const invoiceIsPen = payment?.amount_pen && Number(payment.amount_pen) > 0;
        const invoiceAmount = invoiceIsPen ? Number(payment.amount_pen) : paymentAmount;
        const invoiceCurrency = invoiceIsPen ? 'PEN' : 'USD';

        const { data: ventaInvoice, error: ventaErr } = await supabase.from("invoices").insert({
          flow_type: 'VENTA',
          document_type: 'boleta',
          supplier_name: clientName,
          issue_date: new Date().toISOString().split('T')[0],
          currency: invoiceCurrency,
          subtotal: invoiceAmount / 1.18,
          igv: invoiceAmount - (invoiceAmount / 1.18),
          total: invoiceAmount,
          op_gravada: invoiceAmount / 1.18,
          category: 'ingreso_ventas',
          status: 'pending_invoice',
          payment_id: paymentId,
          reservation_id: payment.reservation_id,
          client_id: clientId ? Number(clientId) : null,
          user_id: currentUser?.id,
          uploaded_by: currentUser?.id,
          sunat_status: 'NO_APLICA',
          file_url: payment.receipt_url || null,
          file_name: payment.receipt_url ? `Voucher pago - ${clientName}` : null,
          notes: `Pago cuota ${cuotaNumber || '—'} • ${destination || 'Reserva'}`,
        }).select().single();
        if (ventaErr) console.error('VENTA invoice insert error:', ventaErr);

        // Auto-generate journal entry for the VENTA
        if (ventaInvoice) {
          try {
            const { generateInvoiceJournalEntry } = await import('../accounting');
            await generateInvoiceJournalEntry(ventaInvoice);
          } catch (journalErr) {
            console.warn('[verifyAndApprovePayment] Auto-journal failed (non-blocking):', journalErr);
          }
        }
      }
    }
  } catch (invoiceErr) {
    // Non-blocking: log but don't fail the approval
    console.warn('Auto-create sales invoice failed (non-blocking):', invoiceErr);
  }

  // 5. Auto-generate payment journal entry (Double-entry recording with Difference of Exchange)
  try {
    if (payment) {
      const { generatePaymentJournalEntry } = await import('../accounting');
      await generatePaymentJournalEntry(payment);
    }
  } catch (journalErr) {
    console.warn('[verifyAndApprovePayment] Auto-journal payment entry failed (non-blocking):', journalErr);
  }

  return data;
};

export const rejectPayment = async (paymentId: string, notes: string) => {
  return await supabase.from("payments").update({ status: 'PAGO_RECHAZADO', notes, verified_at: new Date().toISOString() }).eq('id', paymentId).select('*');
};

// ─── V5 Payment Allocations ─────────────────────────────────────────────────
// Allocations are created at CONFIRMATION time (with schedules), not at payment time.

export const createPaymentAllocations = async (allocations: any[]) => {
  if (!allocations || allocations.length === 0) return { data: [], error: null };
  return await supabase.from("payment_allocations").insert(allocations).select("*");
};

export const fetchPaymentAllocations = async (reservationId: string) => {
  return await supabase
    .from("payment_allocations")
    .select("*")
    .eq("reservation_id", reservationId)
    .order("created_at", { ascending: true });
};

export const assignFlightServicesToReservation = async (services: any[]) => {
  return await supabase.from("reservation_flight_services").insert(services).select("*");
};

export const clearReservationRelations = async (reservationId: string) => {
  const roomDel = await supabase.from("reservation_rooms").delete().eq("reservations_id", reservationId);
  if (roomDel.error) return roomDel;

  const flightSvcDel = await supabase.from("reservation_flight_services").delete().eq("reservations_id", reservationId);
  if (flightSvcDel.error) return flightSvcDel;

  const aeroDel = await supabase.from("reservations_aerolines").delete().eq("reservations_id", reservationId);
  if (aeroDel.error) return aeroDel;

  const hotelDel = await supabase.from("reservations_hotels").delete().eq("reservations_id", reservationId);
  if (hotelDel.error) return hotelDel;

  const tourDel = await supabase.from("reservations_tours").delete().eq("reservations_id", reservationId);
  return tourDel;
};

export const getPaymentSchedulesForRiskAnalysis = async (reservationIds: string[]) => {
  return await supabase
    .from('payment_schedules')
    .select('id, reservation_id, amount, status, due_date')
    .in('reservation_id', reservationIds)
    .in('status', ['PENDIENTE', 'ATRASADO']);
};

export const getReservationsWithPlacesForRiskAnalysis = async (reservationIds: string[]) => {
  return await supabase
    .from('reservations')
    .select('id, places(name)')
    .in('id', reservationIds);
};

export const fetchReservationsTypeByCreatedBy = async (userId: string) => {
  return await supabase.from("reservations").select("type").eq("created_by", userId);
};

export const fetchPaymentsWithFilter = async (filterStatus: string) => {
  let query = supabase
    .from('payments')
    .select('*')
    .order('created_at', { ascending: false });

  if (filterStatus !== 'ALL') {
    query = query.eq('status', filterStatus);
  }

  return await query;
};

export const fetchPaymentSchedulesByIds = async (scheduleIds: string[]) => {
  return await supabase
    .from('payment_schedules')
    .select('id, part_number, amount, due_date, status, reservation_id')
    .in('id', scheduleIds);
};

export const fetchReservationsForVerification = async (reservationIds: string[]) => {
  return await supabase
    .from('reservations')
    .select('id, status, total_price, clients(id, first_name, last_name, name, avatar_url), places(name, country, cover_image_url, image_url)')
    .in('id', reservationIds);
};

export const fetchCruiseReservations = async () => {
  return await supabase
    .from('reservations_cruises')
    .select(`
      *,
      reservations (
        id,
        status,
        created_at,
        customer_id,
        user_id,
        clients (
          id,
          name,
          first_name,
          last_name,
          email,
          avatar_url
        ),
        payment_schedules ( id, reservation_id, part_number, amount, due_date, status ),
        payments ( id, reservation_id, amount, status, method, paid_at )
      )
    `)
    .order('created_at', { ascending: false });
};

export const deleteCruiseReservation = async (cruiseId: number, reservationId?: string) => {
  const { error: cruiseErr } = await supabase.from('reservations_cruises').delete().eq('id', cruiseId);
  if (cruiseErr) return { error: cruiseErr };
  if (reservationId) {
    const { error: resErr } = await supabase.from('reservations').delete().eq('id', reservationId);
    if (resErr) return { error: resErr };
  }
  return { error: null };
};

export const fetchReservationsStats = async () => {
  return await supabase.from("reservations").select("user_id, created_by, status");
};

export { processCruiseItinerary } from './cruises';

